<template>
  <div class="w-full max-w-2xl mx-auto">
    <!-- Step Indicator -->
    <div class="mb-8">
      <div class="flex items-center justify-between">
        <template v-for="(step, index) in steps" :key="step.key">
          <!-- Step Circle -->
          <div class="flex flex-col items-center">
            <div
              :class="[
                'w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-200',
                index < currentStep
                  ? 'bg-bubba-blue text-white'
                  : index === currentStep
                    ? 'bg-bubba-blue text-white ring-4 ring-bubba-blue/20'
                    : 'bg-muted text-muted-foreground'
              ]"
            >
              <Icon v-if="index < currentStep" name="tabler:check" size="18" />
              <span v-else>{{ index + 1 }}</span>
            </div>
            <span
              :class="[
                'mt-2 text-xs font-medium hidden sm:block',
                index <= currentStep ? 'text-foreground' : 'text-muted-foreground'
              ]"
            >
              {{ $t(`vet.signup.steps.${step.key}`) }}
            </span>
          </div>

          <!-- Connector Line -->
          <div
            v-if="index < steps.length - 1"
            :class="[
              'flex-1 h-0.5 mx-2 transition-colors duration-200',
              index < currentStep ? 'bg-bubba-blue' : 'bg-muted'
            ]"
          />
        </template>
      </div>
    </div>

    <!-- Step Content -->
    <UiCard class="shadow-lg">
      <UiCardContent class="p-6 md:p-8">
        <Transition name="slide" mode="out-in">
          <component
            :is="currentStepComponent"
            :key="currentStep"
            :form-data="formData"
            @update="handleUpdate"
            @next="handleNext"
            @back="handleBack"
            @submit="handleSubmit"
          />
        </Transition>
      </UiCardContent>
    </UiCard>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, type Component } from 'vue';
import type { SignupFormData } from '#composables/vet/useVetSignupWizard';
import StepPetType from './signup/StepPetType.vue';
import StepProblem from './signup/StepProblem.vue';
import StepPetDetails from './signup/StepPetDetails.vue';
import StepOwnerDetails from './signup/StepOwnerDetails.vue';

const emit = defineEmits<{
  complete: [data: SignupFormData];
}>();

const steps = [
  { key: 'petType', component: StepPetType },
  { key: 'problem', component: StepProblem },
  { key: 'petDetails', component: StepPetDetails },
  { key: 'owner', component: StepOwnerDetails },
] as const;

const currentStep = ref(0);

const formData = reactive<SignupFormData>({
  species: '',
  breedSlug: undefined,
  breedName: undefined,
  problemDescription: '',
  petName: '',
  petAge: undefined,
  petWeight: undefined,
  ownerName: '',
  email: '',
  phone: undefined,
  password: '',
  acceptedTerms: false,
});

const currentStepComponent = computed<Component>(() => {
  return steps[currentStep.value]!.component;
});

function handleUpdate(updates: Partial<SignupFormData>) {
  Object.assign(formData, updates);
}

function handleNext() {
  if (currentStep.value < steps.length - 1) {
    currentStep.value++;
  }
}

function handleBack() {
  if (currentStep.value > 0) {
    currentStep.value--;
  }
}

function handleSubmit() {
  emit('complete', { ...formData });
}
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: all 0.2s ease-out;
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}
</style>
